import { defineField, defineType } from 'sanity'
import { richTextSimple, strongEmLink } from '../../data/richText'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import getGalleryPageInfo, { gallerySeoGroup } from '../objects/galleryPageInfo'

import FitmentAnchors from '../../components/FitmentAnchors'
import SingleProduct from '../../components/SingleProduct'
import { useSanityClient } from '../../utils/sanityClient'
import { slugify } from '../../utils/slugify'

// TODO: Add complex fitment guide auto-generation logic after migration
// const autoGenerateFitmentGuide = async (document, isHidden) => { ... }

export default defineType({
  name: 'generation',
  title: 'Generation',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
    gallerySeoGroup,
  ],
  fields: [
    defineField({
      name: 'name',
      title: 'Generation Name',
      type: 'string',
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      title: 'Album Title Preference',
      name: 'albumTitlePreference',
      type: 'string',
      description:
        '{make}, {model}, {generation}, {submodel} variables can be used. This pattern is used for image album and fitment guide names. Defaults to "{make} {generation} {model}" if not set.',
      group: 'content',
    }),
    defineField({
      name: 'years',
      title: 'Years',
      type: 'years',
      hidden: ({ parent }) => parent?.hasSubmodels,
      group: 'content',
    }),
    defineField({
      title: 'Has Submodels?',
      name: 'hasSubmodels',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'submodels',
      title: 'Sub-Models',
      type: 'array',
      of: [
        {
          type: 'reference',
          to: [
            {
              type: 'submodel',
            },
          ],
        },
      ],
      hidden: ({ parent }) => !parent?.hasSubmodels,
      group: 'content',
    }),
    defineField({
      name: 'vehiclePageTitle',
      title: 'Vehicle Title',
      description: 'Shows as configurator title and on vehicle index pages',
      type: 'string',
      group: 'content',
    }),
    defineField({
      name: 'vehicleShortDescription',
      title: 'Vehicle Short Description',
      type: 'array',
      of: strongEmLink,
      group: 'content',
      description:
        'Shows below title. Page defaults to Meta Description if not populated.',
    }),
    defineField({
      name: 'trim',
      title: 'Trim list',
      description: 'Shows on vehicle index pages',
      type: 'text',
      rows: 3,
      group: 'content',
    }),
    defineField({
      title: 'Use Different Landing Page?',
      name: 'isRedirect',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      title: 'Point To',
      name: 'redirect',
      description: 'The Canonical URL will be /vehicles/:make/:target-model.',
      type: 'reference',
      to: [{ type: 'generation' }],
      hidden: ({ parent }) => !parent?.isRedirect,
      options: {
        disableNew: true,
      },
      group: 'content',
    }),
    defineField({
      title: 'Enable Landing Page?',
      name: 'isLanding',
      type: 'boolean',
      initialValue: false,
      hidden: ({ parent }) => parent?.isRedirect,
      group: 'content',
    }),
    defineField({
      ...getPageInfo({ path: 'generation' }),
      hidden: ({ parent }) => !parent?.isRedirect && !parent?.isLanding,
    }),
    getGalleryPageInfo(),
    defineField({
      name: 'productOverview',
      title: 'Product Overview',
      type: 'array',
      of: richTextSimple,
      group: 'content',
    }),
    defineField({
      name: 'photo',
      title: 'Photo',
      type: 'image',
      options: {
        hotspot: true,
      },
      description: 'Used for thumbnails on the /vehicles/:make/:model page.',
      hidden: ({ parent }) => !parent?.isRedirect && !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      title: 'Videos',
      name: 'videos',
      type: 'array',
      of: [{ type: 'youtubeVideo' }],
      group: 'content',
    }),
    defineField({
      title: 'Fitment URL',
      name: 'fitmentURL',
      type: 'url',
      description:
        'For now it should point to support.apexraceparts.com/... (Until we migrate fitment guides to Sanity',
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      title: 'Fitment URL Anchors',
      name: 'fitmentURLAnchors',
      type: 'string',
      components: {
        input: FitmentAnchors,
      },
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      title: 'Autogenerate Fitment Guide',
      name: 'autogenerateFitmentGuide',
      description:
        'If you want to assign previously created fitment guide, uncheck this box and select it from the list below.',
      type: 'boolean',
      initialValue: true,
      group: 'content',
    }),
    defineField({
      title: 'Fitment guide',
      name: 'fitmentGuide',
      type: 'reference',
      weak: true, // since this will be readOnly once generated, we want to be able to delete generation document without this reference interfering with it
      to: [{ type: 'fitmentGuide' }],
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      // TODO: Add complex readOnly logic with auto-generation after migration
      // readOnly: ({ document }) => {
      //   const isHidden = document?.isRedirect || !document?.isLanding
      //   if (document.autogenerateFitmentGuide !== false) {
      //     autoGenerateFitmentGuide(document, isHidden)
      //   }
      //   return !!document?.fitmentGuide
      // },
      options: {
        filter: `!(_id in path('drafts.**')) && count(*[_type == 'generation' && references(^._id)]) == 0`,
      },
      group: 'content',
    }),
    defineField({
      title: 'Autogenerate Buyer Guide',
      name: 'autogenerateBuyerGuide',
      description:
        'If you want to assign previously created buyer guide, uncheck this box and select it from the list below.',
      type: 'boolean',
      initialValue: true,
      group: 'content',
    }),
    defineField({
      title: 'Buyer guide',
      name: 'buyerGuide',
      type: 'reference',
      weak: true, // since this will be readOnly once generated, we want to be able to delete generation document without this reference interfering with it
      to: [{ type: 'buyerGuide' }],
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      // TODO: Add complex readOnly logic with auto-generation after migration
      // readOnly: ({ document }) => {
      //   const isHidden = document?.isRedirect || !document?.isLanding
      //   if (document.autogenerateBuyerGuide !== false) {
      //     autoGenerateBuyerGuide(document, isHidden)
      //   }
      //   return !!document?.buyerGuide
      // },
      options: {
        filter: `!(_id in path('drafts.**')) && count(*[_type == 'generation' && references(^._id)]) == 0`,
      },
      group: 'content',
    }),
    defineField({
      title: 'Testimonials',
      name: 'testimonials',
      type: 'testimonials',
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      name: 'configuration',
      title: 'Configuration',
      type: 'reference',
      to: [
        {
          type: 'configuration',
        },
      ],
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      name: 'boltPattern',
      title: 'Bolt pattern',
      type: 'reference',
      to: [{ type: 'boltPattern' }],
      group: 'content',
    }),
    defineField({
      name: 'centerBores',
      title: 'Center Bores',
      description: `If there's only 1 center bore it will be used for both front and rear wheels. If there's 2 center bores first one will be used for front wheels and second one for rear wheels.`,
      type: 'array',
      of: [
        {
          name: 'centerBore',
          title: 'Center Bore',
          type: 'reference',
          to: [{ type: 'centerBore' }],
        },
      ],
      validation: (rule) => rule.min(1).max(2),
      group: 'content',
    }),
    defineField({
      name: 'requiredAccessories',
      title: 'Required Accessories',
      description: `Selected products will be placed in the cart automatically along with each purchased wheel.`,
      type: 'array',
      of: [
        {
          type: 'object',
          title: 'Required product',
          name: 'requiredAccessory',
          fields: [
            defineField({
              title: 'Quantity per wheel',
              name: 'quantity',
              type: 'number',
              validation: (rule) => rule.required(),
            }),
            defineField({
              name: 'product',
              title: 'Product',
              type: 'object',
              fields: [
                defineField({
                  name: 'id',
                  type: 'number',
                }),
                defineField({
                  name: 'sku',
                  type: 'string',
                }),
                defineField({
                  name: 'name',
                  type: 'string',
                }),
              ],
              components: {
                input: SingleProduct,
              },
              validation: (rule) =>
                rule.required().custom((requiredProduct) => {
                  if (!requiredProduct?.id) {
                    return 'Required product id is required'
                  }
                  if (!requiredProduct?.sku) {
                    return 'Required product sku is required'
                  }
                  if (!requiredProduct?.name) {
                    return 'Required product name is required'
                  }
                  return true
                }),
            }),
          ],
          preview: {
            select: {
              quantity: 'quantity',
              product: 'product',
            },
            prepare({ product = {}, quantity }) {
              if (!product.sku) {
                return {
                  title: 'No product selected',
                  subtitle: '',
                }
              }
              return {
                title: `${quantity} x ${product?.sku}`,
                subtitle: product?.name,
              }
            },
          },
        },
      ],
      validation: (rule) => rule.min(1),
      group: 'content',
    }),
    defineField({
      title: 'Hero',
      name: 'hero',
      type: 'hero',
      hidden: ({ parent }) => parent?.isRedirect || !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      title: 'FAQ',
      name: 'faq',
      type: 'faq',
      group: 'content',
    }),
    defineField({
      title: 'Blog',
      name: 'blog',
      type: 'object',
      fields: [
        defineField({
          title: 'Section title',
          name: 'title',
          type: 'string',
          initialValue: 'Popular Articles',
        }),
        defineField({
          title: 'Articles',
          name: 'articles',
          description:
            'For new models only articles assigned to all vehicles can be selected because at that point there should be no articles tagged for this specific model.',
          type: 'array',
          of: [
            {
              type: 'reference',
              to: [{ type: 'article' }],
              // TODO: Add complex filter logic after migration
              // options: {
              //   filter: ({ document }) => {
              //     // Complex filter logic for article selection
              //   },
              // },
            },
          ],
          validation: (rule) => [
            rule.unique().error('Each article should be unique'),
          ],
        }),
        defineField({
          title: 'Button list',
          name: 'buttonList',
          type: 'buttonList',
        }),
      ],
      group: 'content',
    }),
    defineField({
      title: 'Below "Specifications" common blocks',
      name: 'belowSpecificationsCommonBlocks',
      type: 'reference',
      to: [{ type: 'commonPageBuilder' }],
      group: 'content',
    }),
    defineField({
      title: 'Above "Warranty" common blocks',
      name: 'aboveWarrantyCommonBlocks',
      type: 'reference',
      to: [{ type: 'commonPageBuilder' }],
      group: 'content',
    }),
  ],
  preview: {
    select: {
      title: 'name',
      isRedirect: 'isRedirect',
      url: 'redirect.name',
    },
    prepare({ title, isRedirect = false, url = '' }) {
      const subtitle = isRedirect ? `-> ${url}` : ''
      return {
        title,
        subtitle,
      }
    },
  },
})